import { useState, useRef, useEffect } from "react";

const DIAS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function parseLocal(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export default function DatePicker({ value, onChange, placeholder = "dd/mm/aaaa", min, max }) {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => parseLocal(value) || new Date());
  const ref = useRef(null);

  useEffect(() => {
    if (value) setViewDate(parseLocal(value));
  }, [value]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = parseLocal(value);
  const minDate = parseLocal(min);
  const maxDate = parseLocal(max);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  // Primer día del mes (ajustado a lunes=0)
  const firstDay = new Date(year, month, 1).getDay();
  const offset = firstDay === 0 ? 6 : firstDay - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const select = (day) => {
    if (!day) return;
    const date = new Date(year, month, day);
    if (minDate && date < minDate) return;
    if (maxDate && date > maxDate) return;
    onChange(toISO(date));
    setOpen(false);
  };

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

  const displayValue = selected
    ? selected.toLocaleDateString('es-ES')
    : '';

  const isDisabled = (day) => {
    if (!day) return false;
    const d = new Date(year, month, day);
    if (minDate && d < minDate) return true;
    if (maxDate && d > maxDate) return true;
    return false;
  };

  const isSelected = (day) => {
    if (!day || !selected) return false;
    return selected.getFullYear() === year && selected.getMonth() === month && selected.getDate() === day;
  };

  const isToday = (day) => {
    if (!day) return false;
    const t = new Date();
    return t.getFullYear() === year && t.getMonth() === month && t.getDate() === day;
  };

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          background: '#f9fafb',
          border: `1px solid ${open ? '#4f6cf7' : '#d1d5db'}`,
          borderRadius: 12,
          color: displayValue ? '#111827' : '#6b7280',
          padding: '10px 12px',
          cursor: 'pointer',
          userSelect: 'none',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 14,
        }}
      >
        <span>{displayValue || placeholder}</span>
        <span style={{ fontSize: 16 }}>📅</span>
      </div>

      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          left: 0,
          zIndex: 1000,
          background: '#ffffff',
          border: '1px solid #d1d5db',
          borderRadius: 12,
          padding: 12,
          width: 260,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}>
          {/* Cabecera mes/año */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <button type="button" onClick={prevMonth} style={navBtn}>‹</button>
            <span style={{ color: '#111827', fontWeight: 600, fontSize: 13 }}>
              {MESES[month]} {year}
            </span>
            <button type="button" onClick={nextMonth} style={navBtn}>›</button>
          </div>

          {/* Días de la semana */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
            {DIAS.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 11, color: '#6b7280', padding: '2px 0' }}>{d}</div>
            ))}
          </div>

          {/* Celdas */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {cells.map((day, i) => {
              const disabled = isDisabled(day);
              const sel = isSelected(day);
              const today = isToday(day);
              return (
                <div
                  key={i}
                  onClick={() => !disabled && select(day)}
                  style={{
                    textAlign: 'center',
                    padding: '5px 2px',
                    borderRadius: 6,
                    fontSize: 12,
                    cursor: day && !disabled ? 'pointer' : 'default',
                    background: sel ? '#4f6cf7' : today ? '#eff2ff' : 'transparent',
                    color: !day ? 'transparent' : disabled ? '#d1d5db' : sel ? '#fff' : today ? '#4f6cf7' : '#111827',
                    fontWeight: sel || today ? 600 : 400,
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { if (day && !disabled && !sel) e.currentTarget.style.background = '#f3f4f6'; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = today ? '#eff2ff' : 'transparent'; }}
                >
                  {day || ''}
                </div>
              );
            })}
          </div>

          {/* Botón hoy */}
          <div style={{ marginTop: 8, borderTop: '1px solid #e5e7eb', paddingTop: 8, textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => { onChange(toISO(new Date())); setOpen(false); }}
              style={{ background: 'none', border: 'none', color: '#4f6cf7', cursor: 'pointer', fontSize: 12 }}
            >
              Hoy
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const navBtn = {
  background: 'none',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  color: '#374151',
  cursor: 'pointer',
  padding: '2px 8px',
  fontSize: 16,
  lineHeight: 1,
};
